/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars -- Mapping UI is under active iteration; defer strict lint cleanup to avoid behavior changes. */
// UI Components
import React, { useMemo, useState } from 'react';
import { Flex, Table } from '@/components/ui';
import { IconArrowRight } from '@tabler/icons-react';

// Local components
import { FileInfoBlock } from './FileInfoBlock';
import { RequiredMetadataChips } from './RequiredMetadataChips';
import { FormSelector } from './FormSelector';

// New selector components
// New modal for selecting column for required metadata
// New modal for selecting form field for column mapping
// Utils
// highlightMatchingParts now handled in FormSelector
import { buildFormTree } from './utils/formTreeUtils';

// Mapping Engine
import { MappingEngineAdapters } from '../mapping-engine/adapters';
import { MappingEngine } from '../mapping-engine/MappingEngine';
import { calculateFileMappingStatus } from '../utils/mappingStatus';
import { validateAndApplyMapping } from '../utils/mappingValidation';
import { getElementOpacity } from '../utils/hoverHighlighting';

// New smaller components
import { MappingStatusBadge } from './MappingStatusBadge.tsx';
import { MappingActions } from './MappingActions';
import { ModalsContainer } from './ModalsContainer';

// Types
import {
  ColumnMapping,
  ColumnMappingType,
  FileToFormMapping,
  SelectedFile,
  StudyForm,
} from './types';

// Helper function to get metadata label from type
const getMetadataLabel = (metadataType: string): string => {
  const metadataLabels: Record<string, string> = {
    externalSubjectId: 'External Subject ID',
    subjectNumber: 'Subject Number',
    siteNumber: 'Site Number',
    formId: 'Form ID',
    mainFormId: 'Main Form ID',
    siteName: 'Site Name',
    subjectData: 'Subject Data',
    deletedForm: 'Deleted Form',
    formIndex: 'Form Index',
    site: 'Site',
    trash: 'Trash',
  };
  return metadataLabels[metadataType] || metadataType;
};

interface FormMappingCardProps {
  file: SelectedFile;
  availableForms: StudyForm[];
  mapping?: FileToFormMapping;
  columnMappings?: ColumnMapping[];
  onMappingChange: (
    fileId: string,
    formId: string | number | null,
    nestedPath?: string
  ) => void;
  onColumnClassify?: (
    columnName: string,
    type: ColumnMappingType,
    metadataTypeOrTargetField?: string,
    skipBulkConfirmation?: boolean
  ) => void;
  onUpdateState?: (updates: any) => void;
  onPreviewFile: (file: SelectedFile) => void;
  onRemoveFile: (file: SelectedFile) => void;
  index?: string;
  autoMappingEnabled?: boolean;
}

export const FormMappingCard: React.FC<FormMappingCardProps> = ({
  file,
  availableForms,
  mapping,
  columnMappings = [],
  onMappingChange,
  onColumnClassify,
  onUpdateState,
  onPreviewFile,
  onRemoveFile,
  index = '#',
  autoMappingEnabled = false,
}) => {
  const fileId =
    file.source === 'uploaded' ? file.file!.name : `csv-${file.csvFile!.id}`;
  const fileName =
    file.source === 'uploaded' ? file.file!.name : file.csvFile!.filename;
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [formDetailsModalOpen, setFormDetailsModalOpen] = useState(false);
  const [columnClassifyModalOpen, setColumnClassifyModalOpen] = useState(false);
  const [selectedColumnForClassify, setSelectedColumnForClassify] = useState<
    string | null
  >(null);

  // Column selection modal for required metadata
  const [columnSelectionModalOpen, setColumnSelectionModalOpen] =
    useState(false);
  const [selectedMetadataForColumn, setSelectedMetadataForColumn] = useState<{
    type: string;
    label: string;
  } | null>(null);

  // Form field selection modal for columns
  const [formFieldSelectionModalOpen, setFormFieldSelectionModalOpen] =
    useState(false);
  const [selectedColumnForFormField, setSelectedColumnForFormField] = useState<
    string | null
  >(null);
  const [selectedFieldForMapping, setSelectedFieldForMapping] = useState<
    string | null
  >(null);

  // Bulk mapping confirmation dialog
  const [bulkMappingDialogOpen, setBulkMappingDialogOpen] = useState(false);
  const [pendingMappingChange, setPendingMappingChange] = useState<{
    columnName: string;
    mappingType: ColumnMappingType;
    targetField?: string;
    similarMappingsCount: number;
  } | null>(null);

  const selectedForm = useMemo(() => {
    if (!mapping) return null;
    return availableForms.find(form => form.id === mapping.formId) || null;
  }, [mapping, availableForms]);

  // Hover highlighting state
  const [hoveredElement, setHoveredElement] = useState<{
    type: 'column' | 'field' | 'metadata';
    name: string;
  } | null>(null);

  // Get mappings for this file (needed by effects and UI)
  const fileMappings = useMemo(() => {
    return columnMappings.filter(m => m.fileId === fileId);
  }, [columnMappings, fileId]);

  // Perform auto-mapping of columns when form changes
  const performAutoMapping = React.useCallback(
    (form: StudyForm, file: SelectedFile) => {
      if (!onColumnClassify || !form) return;

      try {
        // Use the same MappingEngine approach as global auto-mapping for consistency
        const mappingEngineFile =
          MappingEngineAdapters.selectedFileToMappingEngineFile(file);
        const mappingEngineForm =
          MappingEngineAdapters.studyFormToMappingEngineForm(form);

        // Create initial mapping
        const initialMapping = MappingEngine.createMapping(
          file.file?.name || file.csvFile?.filename || 'unknown',
          mappingEngineForm
        );

        // Auto-map columns using the same logic as global auto-mapping
        const finalMapping = MappingEngine.autoMapColumns(
          initialMapping,
          mappingEngineForm,
          file.metadata.columns, // Pass actual columns from file
          undefined, // sampleData will be taken from file.headerData
          file // Pass file for access to headerData
        );

        // Convert column mappings to legacy format and apply them only to this file
        const newLegacyMappings: any[] = [];
        Object.values(finalMapping.columnMappings).forEach(
          (columnMapping: any) => {
            newLegacyMappings.push({
              fileId: fileId,
              columnName: columnMapping.columnName,
              mappingType: columnMapping.mapping.type,
              confidence: columnMapping.confidence,
              targetField:
                columnMapping.mapping.type === 'formField'
                  ? columnMapping.mapping.targetField?.dataPoint
                  : undefined,
              metadataType:
                columnMapping.mapping.type === 'metadata'
                  ? {
                      type: columnMapping.mapping.metadataType,
                      label: getMetadataLabel(
                        columnMapping.mapping.metadataType
                      ),
                      description: '',
                    }
                  : undefined,
            });
          }
        );

        if (onUpdateState) {
          const filtered = (columnMappings || []).filter(
            m => m.fileId !== fileId
          );
          onUpdateState({
            columnMappings: [...filtered, ...newLegacyMappings],
          });
        }
      } catch {
        // Best-effort: mapping conversion failures shouldn't break the UI.
      }
    },
    [columnMappings, fileId, onColumnClassify, onUpdateState]
  );

  // Clear hovered element when form changes
  const prevFormIdRef = React.useRef<number | undefined>(undefined);
  React.useEffect(() => {
    const currentFormId = selectedForm?.id;
    const prevFormId = prevFormIdRef.current;

    // Auto-mapping logic when form changes - completely disabled if auto-mapping is off
    if (currentFormId && onColumnClassify && autoMappingEnabled) {
      // If this is the first time a form is selected for this file and auto-mapping is enabled
      if (!prevFormId && currentFormId) {
        // Auto-map when first selecting a form
        setTimeout(() => {
          performAutoMapping(selectedForm, file);
        }, 100);
      }
      // If form changed to a different one and auto-mapping is enabled
      else if (prevFormId && String(currentFormId) !== String(prevFormId)) {
        // Reset all mappings to 'ignored' and auto-map
        setTimeout(() => {
          fileMappings.forEach(mapping => {
            if (mapping.fileId === fileId) {
              onColumnClassify(mapping.columnName, 'ignored', undefined, true); // skipBulkConfirmation = true
            }
          });

          // Auto-map columns after reset
          setTimeout(() => {
            performAutoMapping(selectedForm, file);
          }, 100);
        }, 0);
      }
    }

    setHoveredElement(null);
    prevFormIdRef.current = currentFormId;
  }, [
    selectedForm,
    file,
    fileMappings,
    performAutoMapping,
    onColumnClassify,
    fileId,
    autoMappingEnabled,
  ]);

  const handleClearMapping = () => {
    // Clear form selection
    onMappingChange(fileId, null);
    // Clear all column mappings for this file (with delay to avoid conflicts)
    if (onColumnClassify) {
      setTimeout(() => {
        fileMappings.forEach(mapping => {
          if (mapping.fileId === fileId) {
            onColumnClassify(mapping.columnName, 'ignored', undefined, true); // skipBulkConfirmation = true
          }
        });
      }, 0);
    }
  };

  const handlePreview = () => {
    setPreviewModalOpen(true);
  };

  const handleRemove = () => {
    onRemoveFile(file);
  };

  const handleFormDetails = () => {
    setFormDetailsModalOpen(true);
  };

  // Auto-map just the form using MappingEngine (same as global flow)
  const handleAutoMapForm = () => {
    try {
      const mappingEngineFile =
        MappingEngineAdapters.selectedFileToMappingEngineFile(file);
      const mappingEngineForms = availableForms.map(f =>
        MappingEngineAdapters.studyFormToMappingEngineForm(f)
      );

      const result = MappingEngine.smartAutoMap(
        mappingEngineFile,
        mappingEngineForms
      );

      const detectedFormId = result.fileMapping.formId;
      const detectedForm = availableForms.find(f => f.id === detectedFormId);

      if (detectedForm) {
        onMappingChange(fileId, detectedForm.id);
      }
    } catch {
      // Best-effort: auto-mapping is optional.
    }
  };

  // Auto-map only the columns for currently selected form
  const handleAutoMapColumns = () => {
    if (!selectedForm) return;
    performAutoMapping(selectedForm, file);
  };

  // Auto-map form and columns using MappingEngine smart detection
  const handleAutoMapFormAndColumns = () => {
    try {
      const mappingEngineFile =
        MappingEngineAdapters.selectedFileToMappingEngineFile(file);
      const mappingEngineForms = availableForms.map(f =>
        MappingEngineAdapters.studyFormToMappingEngineForm(f)
      );

      const result = MappingEngine.smartAutoMap(
        mappingEngineFile,
        mappingEngineForms
      );

      const detectedFormId = result.fileMapping.formId;
      const detectedForm = availableForms.find(f => f.id === detectedFormId);

      if (detectedForm) {
        onMappingChange(fileId, detectedForm.id);
        performAutoMapping(detectedForm, file);
      }
    } catch {
      // Best-effort: auto-mapping is optional.
    }
  };

  const handleColumnClick = (
    columnName: string,
    type?: ColumnMappingType,
    metadataType?: string
  ) => {
    if (onColumnClassify && type) {
      onColumnClassify(columnName, type, metadataType);
    }
  };

  // New callback for ColumnFieldSelector
  const handleColumnFieldMappingChange = (
    fileId: string,
    columnName: string,
    mappingType: 'formField' | 'metadata' | 'ignored',
    targetField?: string
  ) => {
    if (onColumnClassify) {
      onColumnClassify(columnName, mappingType, targetField);
    }
  };

  // New callback for FieldColumnSelector
  const handleFieldColumnMappingChange = (
    fileId: string,
    columnName: string,
    mappingType: 'formField' | 'metadata' | 'ignored',
    targetField?: string
  ) => {
    if (onColumnClassify) {
      onColumnClassify(columnName, mappingType, targetField);
    }
  };

  const handleColumnClassifyAsMetadata = (metadataType: string) => {
    if (selectedColumnForClassify && onColumnClassify) {
      if (metadataType) {
        onColumnClassify(selectedColumnForClassify, 'metadata', metadataType);
      }
    }
  };

  const handleColumnClassifyAsFormData = () => {
    if (selectedColumnForClassify && onColumnClassify) {
      onColumnClassify(selectedColumnForClassify, 'formField');
    }
  };

  // Handle column selection for required metadata
  const handleColumnSelectForMetadata = (
    columnName: string,
    applyToAll?: boolean
  ) => {
    if (selectedMetadataForColumn && onColumnClassify) {
      if (applyToAll) {
        // TODO: Implement bulk mapping across all files with this column
        // For now, just apply to current file
        onColumnClassify(
          columnName,
          'metadata',
          selectedMetadataForColumn.type
        );
      } else {
        onColumnClassify(
          columnName,
          'metadata',
          selectedMetadataForColumn.type
        );
      }
      setSelectedMetadataForColumn(null);
    }
  };

  // Handle removing metadata mapping
  const handleRemoveMetadataMapping = () => {
    if (selectedMetadataForColumn && onColumnClassify) {
      // Find existing mapping and remove it
      const existingMapping = fileMappings.find(
        mapping =>
          mapping.mappingType === 'metadata' &&
          mapping.metadataType?.type === selectedMetadataForColumn.type
      );

      if (existingMapping && onColumnClassify) {
        // Call with null to remove the mapping
        onColumnClassify(existingMapping.columnName, 'ignored');
      }
      setSelectedMetadataForColumn(null);
    }
  };

  // Handle opening form field selection modal for column
  const handleColumnSelectForFormField = (columnName: string) => {
    setSelectedColumnForFormField(columnName);
    setFormFieldSelectionModalOpen(true);
  };

  // Find similar mappings across all files
  const findSimilarMappings = (
    columnName: string,
    mappingType: ColumnMappingType,
    targetField?: string
  ) => {
    // This would need access to all columnMappings from wizardState
    // For now, we'll return a mock count
    return Math.floor(Math.random() * 3); // 0-2 similar mappings
  };

  // Apply mapping change using validation utility
  const applyMappingChange = (
    columnName: string,
    mappingType: ColumnMappingType,
    targetField?: string,
    applyToAll: boolean = false
  ) => {
    if (!onColumnClassify) return;

    if (applyToAll) {
      // TODO: Apply to all similar mappings across files
      // For now, just apply to current file
    }

    // Use the validation utility for 1:1 mapping enforcement
    validateAndApplyMapping(
      columnName,
      mappingType,
      targetField,
      columnMappings,
      fileId,
      onColumnClassify
    );
  };

  // Handle bulk mapping dialog response
  const handleBulkMappingResponse = (applyToAll: boolean) => {
    if (!pendingMappingChange) return;

    const { columnName, mappingType, targetField } = pendingMappingChange;
    applyMappingChange(columnName, mappingType, targetField, applyToAll);

    // Reset states
    setBulkMappingDialogOpen(false);
    setPendingMappingChange(null);
    setFormFieldSelectionModalOpen(false);
    setSelectedColumnForFormField(null);
    setSelectedFieldForMapping(null);
  };

  // Handle bulk mapping dialog cancel
  const handleBulkMappingCancel = () => {
    // Reset all states when canceling
    setBulkMappingDialogOpen(false);
    setPendingMappingChange(null);
    setFormFieldSelectionModalOpen(false);
    setSelectedColumnForFormField(null);
    setSelectedFieldForMapping(null);
  };

  // Handle form field/column selection in modal
  const handleFormFieldSelect = (selectedItem: string) => {
    const isSelectingColumn = !!selectedFieldForMapping; // If we have selectedFieldForMapping, we're selecting column for field

    if (isSelectingColumn) {
      // Selecting column for field: selectedItem is column name, selectedFieldForMapping is field name
      const fieldName = selectedFieldForMapping;
      const columnName = selectedItem;

      // Check for similar mappings
      const similarCount = findSimilarMappings(
        columnName,
        'formField',
        fieldName
      );

      if (similarCount > 0) {
        setPendingMappingChange({
          columnName,
          mappingType: 'formField',
          targetField: fieldName,
          similarMappingsCount: similarCount,
        });
        setBulkMappingDialogOpen(true);
        return;
      }

      // Apply mapping directly
      applyMappingChange(columnName, 'formField', fieldName, false);
    } else {
      // Selecting field for column: selectedItem is field name, selectedColumnForFormField is column name
      const columnName = selectedColumnForFormField;
      const fieldName = selectedItem;

      if (!columnName) return;

      // Check for similar mappings
      const similarCount = findSimilarMappings(
        columnName,
        'formField',
        fieldName
      );

      if (similarCount > 0) {
        setPendingMappingChange({
          columnName,
          mappingType: 'formField',
          targetField: fieldName,
          similarMappingsCount: similarCount,
        });
        setBulkMappingDialogOpen(true);
        return;
      }

      // Apply mapping directly
      applyMappingChange(columnName, 'formField', fieldName, false);
    }

    // Reset modal state
    setFormFieldSelectionModalOpen(false);
    setSelectedColumnForFormField(null);
    setSelectedFieldForMapping(null);
  };

  // Handle removing form field mapping for column
  const handleRemoveFormFieldMapping = () => {
    if (!onColumnClassify) return;

    if (selectedColumnForFormField) {
      // Remove mapping for the selected column
      onColumnClassify(selectedColumnForFormField, 'ignored');
    } else if (selectedFieldForMapping) {
      // Remove mapping for the selected field (find and unmap the column)
      const existingMapping = fileMappings.find(
        mapping =>
          mapping.fileId === fileId &&
          mapping.mappingType === 'formField' &&
          mapping.targetField === selectedFieldForMapping
      );

      if (existingMapping) {
        onColumnClassify(existingMapping.columnName, 'ignored');
      }
    }

    // Reset modal state
    setFormFieldSelectionModalOpen(false);
    setSelectedColumnForFormField(null);
    setSelectedFieldForMapping(null);
  };

  // Handle clicking on a form field to open mapping modal
  const handleFieldClick = (fieldDataPoint: string) => {
    // Find if this field is already mapped to a column
    const existingMapping = fileMappings.find(
      mapping =>
        mapping.mappingType === 'formField' &&
        mapping.targetField === fieldDataPoint
    );

    // Set the selected field and column (if mapped) for modal
    setSelectedFieldForMapping(fieldDataPoint);
    setSelectedColumnForFormField(existingMapping?.columnName || null);
    setFormFieldSelectionModalOpen(true);
  };

  const getMatchingColumns = () => {
    if (!selectedForm || !selectedForm.fields) return [];

    const fileColumns = file.metadata.columns;
    const formFields = selectedForm.fields
      .filter(field => field && field.dataPoint)
      .map(field => field.dataPoint);

    return fileColumns.filter(col =>
      formFields.some(
        field => field && field.toLowerCase() === col.toLowerCase()
      )
    );
  };

  const matchingColumns = getMatchingColumns();

  // Build form tree to determine if selected form is a subform
  const formTree = useMemo(() => {
    return availableForms ? buildFormTree(availableForms) : [];
  }, [availableForms]);

  // Determine if selected form is a subform
  const isSelectedFormSubform = useMemo(() => {
    if (!selectedForm || !formTree.length) return false;
    // Check if this form is a child of any root form
    return formTree.some(root =>
      root.children.some(child => child.id === selectedForm.id)
    );
  }, [selectedForm, formTree]);

  // Auto-mapping of columns is now only triggered manually
  // No automatic triggering when selecting a form

  const getColumnMapping = (columnName: string) => {
    return fileMappings.find(m => m.columnName === columnName);
  };

  // Helper function for hover highlighting using utility
  const calculateElementOpacity = (
    elementType: 'column' | 'field' | 'metadata',
    elementName: string
  ) => {
    return getElementOpacity(
      elementType,
      elementName,
      hoveredElement,
      columnMappings,
      fileId
    );
  };

  // Wrapper to match expected signature for components
  const getElementOpacityForComponents = (
    elementType: 'column' | 'field' | 'metadata',
    elementName: string
  ) => {
    return calculateElementOpacity(elementType, elementName);
  };

  const isElementHighlighted = (
    elementType: 'column' | 'field' | 'metadata',
    elementName: string
  ) => {
    return getElementOpacityForComponents(elementType, elementName) === 1;
  };

  // Get available columns for metadata selection
  const getAvailableColumnsForMetadata = useMemo(() => {
    return file.metadata.columns.map(columnName => {
      const mapping = fileMappings.find(m => m.columnName === columnName);
      // Consider column "already mapped" only if it's not ignored and has a meaningful mapping
      const isAlreadyMapped = !!(mapping && mapping.mappingType !== 'ignored');
      return {
        name: columnName,
        isAlreadyMapped,
        currentMapping: mapping || undefined,
      };
    });
  }, [file.metadata.columns, fileMappings]);

  // Calculate file mapping status using utility
  const fileMappingStatus = useMemo(() => {
    return calculateFileMappingStatus(
      selectedForm,
      file.metadata.columns,
      columnMappings,
      fileId,
      isSelectedFormSubform
    );
  }, [
    selectedForm,
    file.metadata.columns,
    columnMappings,
    fileId,
    isSelectedFormSubform,
  ]);

  // Get column classification from existing mappings
  const getColumnClassification = (columnName: string) => {
    const mapping = columnMappings.find(
      m => m.fileId === fileId && m.columnName === columnName
    );
    return mapping?.mappingType || 'ignored';
  };

  const getColumnBadgeColor = (columnName: string) => {
    // If no form is selected, don't show any column as mapped
    if (!selectedForm) return 'default';

    const classification = getColumnClassification(columnName);

    // Only show special colors for actually mapped columns
    if (classification === 'metadata') return 'info';
    if (classification === 'formField') return 'success';
    if (classification === 'ignored') return 'default';

    // All unmapped columns (regardless of potential matches) should be default color
    return 'default';
  };

  const getColumnBadgeVariant = (columnName: string) => {
    // If no form is selected, show as unmapped
    if (!selectedForm) return 'dashed';

    const classification = getColumnClassification(columnName);

    // Explicit mappings take precedence - only show special styling for actually mapped columns
    if (classification === 'metadata') return 'surface'; // blue surface for metadata
    if (classification === 'formField') return 'surface'; // green surface for form fields
    if (classification === 'ignored') return 'soft'; // soft for ignored

    // All unmapped columns (regardless of potential matches) should be dashed
    return 'dashed';
  };

  const getFilesWithColumn = (columnName: string) => {
    // This would normally come from all files, but for now we'll mock it
    return Math.floor(Math.random() * 5) + 1; // 1-5 files
  };

  return (
    <>
      <Table.Row

      // className={`form-mapping-card ${fileMappingStatus.status === 'excellent' ? 'mapping-excellent' : ''}`}
      >
        <Table.Cell>
          <MappingStatusBadge
            index={index}
            fileMappingStatus={fileMappingStatus}
            selectedForm={selectedForm}
          />
        </Table.Cell>
        {/*<Flex direction="column" gap="4">*/}

        {/* Mapping Interface */}
        {/* <Grid
          style={{
            minHeight: '100px',
            // display: 'grid',
            display: 'contents',
            gridTemplateColumns: '1fr auto 1fr',
            gap: '10px',
            alignItems: 'flex-start',
          }}
        >*/}
        {/* File Info Block */}
        <Table.Cell>
          <FileInfoBlock
            file={file}
            showColumns={true}
            matchingColumns={matchingColumns}
            getColumnColor={getColumnBadgeColor}
            getColumnVariant={getColumnBadgeVariant}
            getColumnMapping={getColumnMapping}
            onColumnClick={onColumnClassify ? handleColumnClick : undefined}
            onColumnSelectForFormData={handleColumnSelectForFormField}
            className="file-info-block"
            handlePreview={handlePreview}
            isSubform={isSelectedFormSubform}
            hoveredElement={hoveredElement}
            onElementHover={setHoveredElement}
            getElementOpacity={getElementOpacityForComponents}
            // New props for enhanced selectors
            useNewSelector={true}
            form={selectedForm ?? undefined}
            fileMappings={columnMappings}
            fileId={fileId}
            onColumnFieldMappingChange={handleColumnFieldMappingChange}
          />
        </Table.Cell>
        <Table.Cell>
          {/* Arrow */}
          <Flex align="center" justify="center" style={{ minHeight: '40px' }}>
            <IconArrowRight size={24} color="var(--gray-9)" />
          </Flex>
        </Table.Cell>

        {/* Form Selection Block */}
        <FormSelector
          selectedForm={selectedForm}
          availableForms={availableForms}
          mapping={mapping}
          file={file}
          onFormSelect={(formId, nestedPath) => {
            onMappingChange(
              fileId,
              typeof formId === 'string' ? parseInt(formId) : formId,
              nestedPath
            );
          }}
          onClearMapping={handleClearMapping}
          className="form-info-block"
          showFieldMatching={true}
          hoveredElement={hoveredElement}
          onElementHover={setHoveredElement}
          getElementOpacity={getElementOpacityForComponents}
          onFieldClick={handleFieldClick}
          columnMappings={columnMappings}
          fileId={fileId}
          // New props for enhanced selectors
          useNewSelector={true}
          onFieldColumnMappingChange={handleFieldColumnMappingChange}
          useName={true}
        >
          {/* Required Metadata Chips */}
          {selectedForm && (
            <RequiredMetadataChips
              fileId={fileId}
              columnMappings={columnMappings}
              isSubform={isSelectedFormSubform}
              hoveredElement={hoveredElement}
              onElementHover={setHoveredElement}
              getElementOpacity={getElementOpacityForComponents}
              onMetadataSelect={(metadataType, metadataLabel) => {
                // Open column selection modal for required metadata
                setSelectedMetadataForColumn({
                  type: metadataType,
                  label: metadataLabel || metadataType,
                });
                setColumnSelectionModalOpen(true);
              }}
              // New props for enhanced selectors
              useNewSelector={true}
              fileColumns={file.metadata.columns}
              onFieldColumnMappingChange={handleFieldColumnMappingChange}
            />
          )}
        </FormSelector>

        {/*</Grid>*/}

        {/* Actions */}
        <Table.Cell>
          <MappingActions
            selectedForm={selectedForm}
            fileName={fileName}
            onPreview={handlePreview}
            onFormDetails={handleFormDetails}
            onClearMapping={handleClearMapping}
            onRemoveFile={handleRemove}
            onAutoMapForm={handleAutoMapForm}
            onAutoMapColumns={selectedForm ? handleAutoMapColumns : undefined}
            onAutoMapFormAndColumns={handleAutoMapFormAndColumns}
          />
        </Table.Cell>
        {/*</Flex>*/}
      </Table.Row>

      {/* All Modals */}
      <ModalsContainer
        // File Preview Modal
        previewModalOpen={previewModalOpen}
        setPreviewModalOpen={setPreviewModalOpen}
        file={file}
        fileName={fileName}
        // Form Details Modal
        formDetailsModalOpen={formDetailsModalOpen}
        setFormDetailsModalOpen={setFormDetailsModalOpen}
        selectedForm={selectedForm}
        // Column Classification Modal
        columnClassifyModalOpen={columnClassifyModalOpen}
        setColumnClassifyModalOpen={setColumnClassifyModalOpen}
        selectedColumnForClassify={selectedColumnForClassify}
        getFilesWithColumn={getFilesWithColumn}
        isSelectedFormSubform={isSelectedFormSubform}
        onColumnClassifyAsMetadata={handleColumnClassifyAsMetadata}
        onColumnClassifyAsFormData={handleColumnClassifyAsFormData}
        // Column Selection Modal for Required Metadata
        columnSelectionModalOpen={columnSelectionModalOpen}
        setColumnSelectionModalOpen={setColumnSelectionModalOpen}
        selectedMetadataForColumn={selectedMetadataForColumn}
        getAvailableColumnsForMetadata={getAvailableColumnsForMetadata}
        onColumnSelectForMetadata={handleColumnSelectForMetadata}
        onRemoveMetadataMapping={handleRemoveMetadataMapping}
        fileMappings={fileMappings}
        fileId={fileId}
        // Form Field Selection Modal
        formFieldSelectionModalOpen={formFieldSelectionModalOpen}
        setFormFieldSelectionModalOpen={setFormFieldSelectionModalOpen}
        selectedColumnForFormField={selectedColumnForFormField}
        selectedFieldForMapping={selectedFieldForMapping}
        onFormFieldSelect={handleFormFieldSelect}
        onRemoveFormFieldMapping={handleRemoveFormFieldMapping}
        // Bulk Mapping Confirmation Dialog
        bulkMappingDialogOpen={bulkMappingDialogOpen}
        setBulkMappingDialogOpen={setBulkMappingDialogOpen}
        pendingMappingChange={pendingMappingChange}
        handleBulkMappingResponse={handleBulkMappingResponse}
        handleBulkMappingCancel={handleBulkMappingCancel}
      />

      {/*<Divider />*/}
    </>
  );
};

export default FormMappingCard;
